import { calculateTaxFromExVAT } from "./orders";

/**
 * Offertes (BMX-58). Een offerte wordt in de webshop opgebouwd (zelfde mandje en checkout als een
 * bestelling) en staat in Firestore `quotes`, met dezelfde velden als een order plus
 * `quoteNumber`, `version`, `validUntil`, `status` en `sends`. In het CMS kun je hem bekijken,
 * prijzen en aantallen aanpassen, mailen en omzetten in een bestelling. Zie docs/offertes.md.
 *
 * Hier alleen de rekenregels, zonder React of Firestore, zodat ze los te toetsen zijn.
 */

export const QUOTE_STATUS = {
  OPEN: "OPEN",
  CONVERTED: "CONVERTED",
  CANCELLED: "CANCELLED",
};

// Wat de lijst toont: open wordt "verlopen" zodra validUntil voorbij is. Verlopen is geen
// opgeslagen status — omzetten kan dan nog gewoon.
export const QUOTE_DISPLAY_STATUS = {
  OPEN: "open",
  EXPIRED: "expired",
  CONVERTED: "converted",
  CANCELLED: "cancelled",
};

export const QUOTE_VALIDITY_DAYS = 30;
const DAY_SECONDS = 24 * 60 * 60;

const nowSeconds = () => Math.floor(Date.now() / 1000);
const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100;

export const getQuoteValidUntil = (from = nowSeconds()) => from + QUOTE_VALIDITY_DAYS * DAY_SECONDS;

export const isQuoteOpen = (quote) => (quote?.status?.current || QUOTE_STATUS.OPEN) === QUOTE_STATUS.OPEN;

export const getQuoteDisplayStatus = (quote, now = nowSeconds()) => {
  const current = quote?.status?.current || QUOTE_STATUS.OPEN;
  if (current === QUOTE_STATUS.CONVERTED) return QUOTE_DISPLAY_STATUS.CONVERTED;
  if (current === QUOTE_STATUS.CANCELLED) return QUOTE_DISPLAY_STATUS.CANCELLED;
  if (quote?.validUntil && quote.validUntil < now) return QUOTE_DISPLAY_STATUS.EXPIRED;
  return QUOTE_DISPLAY_STATUS.OPEN;
};

export const QUOTE_SEND_STATE = {
  NEVER: "never",
  SENT: "sent",
  // Verstuurd, maar daarna aangepast: de klant heeft een oudere versie
  CHANGED: "changed",
  FAILED: "failed",
};

/**
 * Hoe het met het versturen staat. De API zet `lastSentAt`/`lastSentVersion` bij succes en
 * `lastSendFailedAt`/`lastSendError` bij een mislukte poging (die laatste twee verdwijnen weer
 * bij de volgende geslaagde). Net als bij de orderbevestiging telt de laatste poging.
 */
export const getQuoteSendState = (quote) => {
  const sentAt = quote?.lastSentAt || null;
  const failedAt = quote?.lastSendFailedAt || null;
  if (failedAt && (!sentAt || failedAt > sentAt)) {
    return { state: QUOTE_SEND_STATE.FAILED, at: failedAt, error: quote.lastSendError || null };
  }
  if (!sentAt) return { state: QUOTE_SEND_STATE.NEVER };
  const version = quote.version || 1;
  const sentVersion = quote.lastSentVersion || 1;
  const lastSend = Array.isArray(quote.sends) && quote.sends.length ? quote.sends[quote.sends.length - 1] : null;
  return {
    state: sentVersion < version ? QUOTE_SEND_STATE.CHANGED : QUOTE_SEND_STATE.SENT,
    at: sentAt,
    version: sentVersion,
    to: lastSend?.to || null,
  };
};

// ---------------------------------------------------------------------------
// Prijzen per regel
// ---------------------------------------------------------------------------

/**
 * Een orderregel heeft `originalPrice` (prijs van de klantgroep), `discountPercentage`,
 * `discount` (korting per stuk) en `paymentPrice` (wat de klant betaalt; Exact krijgt deze als
 * stukprijs). Een eigen prijs past daarin: alleen paymentPrice en de korting veranderen, en
 * `manualPrice` markeert de regel. `listPrice` onthoudt wat de webshop rekende, voor "terugzetten".
 */
const withPrice = (line, paymentPrice) => {
  const price = round2(Math.max(0, paymentPrice));
  const original = Number(line.originalPrice) || 0;
  return {
    ...line,
    paymentPrice: price,
    discount: round2(original - price),
    discountPercentage: original > 0 ? round2((1 - price / original) * 100) : 0,
  };
};

export const setLinePrice = (line, paymentPrice) => ({
  ...withPrice(line, paymentPrice),
  manualPrice: true,
  listPrice: line.manualPrice ? line.listPrice : line.paymentPrice,
});

export const setLineDiscountPercentage = (line, percentage) => {
  const original = Number(line.originalPrice) || 0;
  const pct = Math.min(100, Math.max(0, Number(percentage) || 0));
  return {
    ...setLinePrice(line, original * (1 - pct / 100)),
    discountPercentage: pct,
  };
};

export const resetLinePrice = (line) => {
  if (!line.manualPrice) return line;
  const { manualPrice, listPrice, ...rest } = line;
  return withPrice(rest, listPrice ?? line.paymentPrice);
};

/**
 * Een eigen prijs geldt per product, niet per regel: een product kan over twee regels verdeeld
 * zijn (uit voorraad + pre-order), en bij het omzetten worden die opnieuw verdeeld. Met twee
 * prijzen voor één product zou niet meer te zeggen zijn welke eenheid welke prijs heeft.
 */
export const applyLinePriceToProduct = (lines, index, change) => {
  const target = lines[index];
  if (!target) return lines;
  return lines.map((line, i) => (i === index || line.productId === target.productId ? change(line) : line));
};

// ---------------------------------------------------------------------------
// Totalen
// ---------------------------------------------------------------------------

export const getLinesTotal = (lines) =>
  round2((lines || []).reduce((sum, line) => sum + (Number(line.paymentPrice) || 0) * (Number(line.quantity) || 0), 0));

/**
 * Totalen opnieuw uitrekenen na een wijziging, op dezelfde manier als de checkout van de webshop
 * (Checkout.js `getCartTotalForFirestore` en `calculateTaxFromExVAT`):
 * - totalPrice (excl. btw) = regels − kortingscode + verzendkosten − persoonlijke korting;
 * - btw alleen als de offerte btw had (NL); over regels en verzendkosten, niet over de
 *   persoonlijke korting — zo doet de webshop het ook;
 * - een persoonlijke korting met percentage rekent mee met het nieuwe regeltotaal.
 * `deliveryCost` null = "achteraf berekend" (geen standaardtarief voor het land).
 */
export const calculateQuoteTotals = (quote, lines, deliveryCost) => {
  const linesTotal = getLinesTotal(lines);
  const campaign = quote?.campaignDiscount || null;
  const campaignDiscount = campaign
    ? {
        ...campaign,
        amount: campaign.percentage ? round2((linesTotal * campaign.percentage) / 100) : Number(campaign.amount) || 0,
      }
    : null;
  const codeDiscount = Number(quote?.discount?.totalDiscount) || 0;
  const cost = deliveryCost === null || deliveryCost === undefined ? null : round2(deliveryCost);
  const totalPrice = round2(linesTotal - codeDiscount + (cost || 0) - (campaignDiscount?.amount || 0));
  const hasVat = quote?.vat !== null && quote?.vat !== undefined;
  const vat =
    hasVat && lines.length
      ? round2(
          calculateTaxFromExVAT(
            lines,
            cost,
            quote?.discount?.percentage || null,
            quote?.discount?.amount || null,
            quote?.discount?.categories || null,
          ).taxPrice,
        )
      : hasVat
        ? 0
        : null;
  return { linesTotal, campaignDiscount, totalPrice, vat, deliveryCost: cost };
};

// ---------------------------------------------------------------------------
// Omzetten: opnieuw verdelen over voorraad, pre-order en tijdelijk niet leverbaar
// ---------------------------------------------------------------------------

const parseDdMmYyyy = (value) => {
  if (typeof value !== "string") return null;
  const match = value.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (!match) return null;
  const date = new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1]));
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Spiegel van `isPreOrderProgramActive` in webshop/src/helpers/product.js: aan én een
 * beschikbaarheidsdatum in de toekomst. Houd die twee gelijk.
 */
export const isPreOrderProgramActive = (preOrder, now = new Date()) => {
  if (!preOrder || !preOrder.active) return false;
  const availableAt = parseDdMmYyyy(preOrder.availableAt);
  if (!availableAt) return false;
  // moment(...).isAfter(moment()): de datum (middernacht) moet ná nu liggen
  return availableAt.getTime() > now.getTime();
};

/**
 * Spiegel van `splitQuantityOverStockAndPreOrder` in webshop/src/helpers/product.js: eerst
 * voorraad (in hele besteleenheden), de rest pre-order zolang het programma loopt, wat dan nog
 * over is tijdelijk niet leverbaar. Houd die twee gelijk.
 */
export const splitQuantityOverStockAndPreOrder = (
  requestedQty,
  { stock, orderUnit, preOrder, stockAlreadyClaimed = 0 },
  now = new Date(),
) => {
  const unit = orderUnit || 1;
  const requested = Math.max(0, requestedQty || 0);
  const freeStock = Math.max(0, (stock || 0) - Math.max(0, stockAlreadyClaimed || 0));
  const allocatable = Math.floor(freeStock / unit) * unit;
  const normalQty = Math.min(requested, allocatable);
  const remainder = requested - normalQty;
  const preOrderQty = isPreOrderProgramActive(preOrder, now) ? remainder : 0;
  return { normalQty, preOrderQty, unavailableQty: remainder - preOrderQty };
};

export const LINE_KIND = { NOW: "now", PRE_ORDER: "preOrder", UNAVAILABLE: "unavailable" };

export const getLineKind = (line) =>
  line.unavailableLine ? LINE_KIND.UNAVAILABLE : line.preOrderLine ? LINE_KIND.PRE_ORDER : LINE_KIND.NOW;

const sameOrderLine = (a, b) =>
  a.productId === b.productId &&
  getLineKind(a) === getLineKind(b) &&
  round2(a.paymentPrice) === round2(b.paymentPrice) &&
  !!a.manualPrice === !!b.manualPrice;

/**
 * De offerteregels zoals ze in de order komen, verdeeld naar de voorraad van nu. De offerte kan
 * weken oud zijn; wat toen uit voorraad kon, is er misschien niet meer (of andersom).
 *
 * Per regel, in volgorde, met de voorraad die eerdere regels van hetzelfde product al innamen —
 * net als `computeUpdatedCart` in de webshop. Prijzen en aantallen per product blijven gelijk
 * (de API controleert dat); alleen `preOrderLine`/`unavailableLine`, `stock` en `preOrder`
 * veranderen. Een product dat niet meer in de catalogus staat, gaat ongewijzigd mee en komt in
 * `missingProductIds`.
 *
 * @returns {{ lines, missingProductIds, expectedShippingDate, isMixed }}
 */
export const splitQuoteLinesForOrder = (quoteLines, catalogue, now = new Date()) => {
  const byId = new Map((catalogue || []).map((product) => [product.id, product]));
  const claimed = new Map();
  const missingProductIds = [];
  const result = [];

  const push = (line) => {
    const existing = result.find((candidate) => sameOrderLine(candidate, line));
    if (existing) existing.quantity += line.quantity;
    else result.push(line);
  };

  (quoteLines || []).forEach((line) => {
    const product = byId.get(line.productId);
    if (!product) {
      if (!missingProductIds.includes(line.productId)) missingProductIds.push(line.productId);
      push({ ...line });
      return;
    }
    const alreadyClaimed = claimed.get(line.productId) || 0;
    const { normalQty, preOrderQty, unavailableQty } = splitQuantityOverStockAndPreOrder(
      line.quantity,
      {
        stock: product.stock,
        orderUnit: product.orderUnit,
        preOrder: product.preOrder,
        stockAlreadyClaimed: alreadyClaimed,
      },
      now,
    );
    claimed.set(line.productId, alreadyClaimed + normalQty);
    const base = { ...line, stock: product.stock ?? null, preOrder: product.preOrder || null };
    if (normalQty > 0) push({ ...base, quantity: normalQty, preOrderLine: false, unavailableLine: false });
    if (preOrderQty > 0) push({ ...base, quantity: preOrderQty, preOrderLine: true, unavailableLine: false });
    if (unavailableQty > 0) push({ ...base, quantity: unavailableQty, preOrderLine: false, unavailableLine: true });
  });

  // Zoals de checkout: de laatste pre-orderdatum, niet vroeger dan vandaag. Tijdelijk niet
  // leverbaar heeft geen datum en telt hier niet mee.
  const latest = result
    .filter((line) => line.preOrderLine)
    .map((line) => parseDdMmYyyy(line.preOrder?.availableAt))
    .filter(Boolean)
    .reduce((max, date) => (date > max ? date : max), null);
  const expectedShippingDate =
    latest && latest > now
      ? `${String(latest.getDate()).padStart(2, "0")}-${String(latest.getMonth() + 1).padStart(2, "0")}-${latest.getFullYear()}`
      : null;

  const kinds = new Set(result.map(getLineKind));
  const isMixed = kinds.has(LINE_KIND.NOW) && (kinds.has(LINE_KIND.PRE_ORDER) || kinds.has(LINE_KIND.UNAVAILABLE));

  return { lines: result, missingProductIds, expectedShippingDate, isMixed };
};

/** Aantal stuks per soort, voor het overzicht in de omzetdialoog. */
export const countByKind = (lines) =>
  (lines || []).reduce(
    (counts, line) => ({ ...counts, [getLineKind(line)]: counts[getLineKind(line)] + (line.quantity || 0) }),
    { [LINE_KIND.NOW]: 0, [LINE_KIND.PRE_ORDER]: 0, [LINE_KIND.UNAVAILABLE]: 0 },
  );

// ---------------------------------------------------------------------------
// Zoeken
// ---------------------------------------------------------------------------

export const quoteMatchesSearch = (quote, search) => {
  const needle = (search || "").trim().toLowerCase();
  if (!needle) return true;
  const customer = quote?.customer || {};
  return [
    quote?.quoteNumber,
    quote?.orderId,
    customer.company,
    customer.firstName,
    customer.lastName,
    customer.email,
    quote?.salesPersonName,
  ]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(needle));
};
